import { useState, useEffect } from 'react';
import { getTenant, Tenant, PlanId } from '../services/tenantService';

export interface SubscriptionStatus {
  isTrialing: boolean;
  isActive: boolean;
  isExpired: boolean;
  trialDaysLeft: number;
  status: Tenant['subscriptionStatus'];
  planId: PlanId;
  loading: boolean;
}

export type AppFeature = 
  | 'benchmarks' 
  | 'velocity' 
  | 'shift-performance' 
  | 'api-webhooks' 
  | 'multi-location';

const PLAN_FEATURES: Record<PlanId, AppFeature[]> = {
  base: [],
  enhanced: ['benchmarks', 'velocity', 'shift-performance', 'api-webhooks', 'multi-location'],
  enterprise: ['benchmarks', 'velocity', 'shift-performance', 'api-webhooks', 'multi-location'],
};

export const useSubscription = (tenantId: string | null) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [subStatus, setSubStatus] = useState<SubscriptionStatus>({
    isTrialing: false,
    isActive: false,
    isExpired: false,
    trialDaysLeft: 0,
    status: 'none',
    planId: 'base',
    loading: true
  });

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refetch = () => setRefreshTrigger(prev => prev + 1);

  useEffect(() => {
    if (!tenantId || tenantId === 'undefined' || tenantId === 'null') {
      setSubStatus({
        isTrialing: true,
        isActive: true,
        isExpired: false,
        trialDaysLeft: 14,
        status: 'trialing',
        planId: 'enterprise',
        loading: false
      });
      return;
    }

    const checkSubscription = async () => {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Subscription check timed out')), 15000)
      );

      try {
        const tenantData = await Promise.race([
          getTenant(tenantId),
          timeoutPromise
        ]) as Tenant | null;

        if (!tenantData) {
          setSubStatus({
            isTrialing: true,
            isActive: true,
            isExpired: false,
            trialDaysLeft: 14,
            status: 'trialing',
            planId: 'enterprise',
            loading: false
          });
          return;
        }
        setTenant(tenantData);

        const now = new Date();
        const trialEnd = tenantData.trialEndsAt?.toDate ? tenantData.trialEndsAt.toDate() : new Date(tenantData.trialEndsAt);
        
        let trialDaysLeft = 0;
        if (!isNaN(trialEnd.getTime())) {
          trialDaysLeft = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        }
        
        const isTrialing = tenantData.subscriptionStatus === 'trialing';
        const isActive = tenantData.subscriptionStatus === 'active';
        const isExpired = (isTrialing && trialDaysLeft <= 0) || tenantData.subscriptionStatus === 'canceled' || tenantData.subscriptionStatus === 'past_due';

        setSubStatus({
          isTrialing: isTrialing || (!isTrialing && !isActive),
          isActive: isActive || isTrialing,
          isExpired: isExpired && !isTrialing,
          trialDaysLeft: Math.max(0, trialDaysLeft) || 14,
          status: tenantData.subscriptionStatus || 'trialing',
          planId: tenantData.planId || 'enterprise',
          loading: false
        });
      } catch (error) {
        console.error('Error checking subscription:', error);
        setSubStatus({
          isTrialing: true,
          isActive: true,
          isExpired: false,
          trialDaysLeft: 14,
          status: 'trialing',
          planId: 'enterprise',
          loading: false
        });
      }
    };

    checkSubscription();
  }, [tenantId, refreshTrigger]);

  const hasFeature = (feature: AppFeature): boolean => {
    const plan = tenant?.planId || 'base';
    // Handle potential legacy planId or unexpected values
    let normalizedPlan = plan;
    
    // Ensure the plan is a valid PlanId
    if (normalizedPlan !== 'base' && normalizedPlan !== 'enhanced' && normalizedPlan !== 'enterprise') {
      normalizedPlan = 'base';
    }

    const features = PLAN_FEATURES[normalizedPlan as PlanId];
    return features?.includes(feature) ?? false;
  };

  return { ...subStatus, hasFeature, refetch };
};
